/**
 * A `;` only ends a statement when it is not inside a quoted region, a comment,
 * or a bracket. Scanning has to skip comments as well as strings: an apostrophe
 * in `// the tag's own path` would otherwise open a string literal that swallows
 * everything up to the next quote, and every terminator after it.
 */
const QUOTES = new Set(["'", '"', '`'])

/** Index just past the closing quote, or the end of input if unterminated. */
function skipQuoted(s: string, openIndex: number): number {
  const quote = s[openIndex]!
  let i = openIndex + 1
  while (i < s.length) {
    const ch = s[i]!
    if (ch === '\\' && i + 1 < s.length) {
      i += 2
      continue
    }
    if (ch === quote) return i + 1
    i += 1
  }
  return s.length
}

/** Index just past the comment that starts at `openIndex`, or -1 if none starts there. */
function skipComment(s: string, openIndex: number): number {
  if (s[openIndex] !== '/') return -1
  if (s[openIndex + 1] === '/') {
    const newline = s.indexOf('\n', openIndex)
    return newline === -1 ? s.length : newline
  }
  if (s[openIndex + 1] === '*') {
    const close = s.indexOf('*/', openIndex + 2)
    return close === -1 ? s.length : close + 2
  }
  return -1
}

/** Whether a fragment holds anything Neo4j would execute, ignoring comments. */
function hasExecutableText(part: string): boolean {
  let i = 0
  while (i < part.length) {
    const afterComment = skipComment(part, i)
    if (afterComment !== -1) {
      i = afterComment
      continue
    }
    if (!/\s/.test(part[i]!)) return true
    i += 1
  }
  return false
}

/** Split pasted Cypher into individual write statements on top-level `;`. */
export function splitCypherStatements(raw: string): string[] {
  const out: string[] = []
  let start = 0
  let parenDepth = 0
  let braceDepth = 0
  let bracketDepth = 0
  let i = 0

  const push = (end: number) => {
    const part = raw.slice(start, end).trim()
    if (part.length > 0 && hasExecutableText(part)) out.push(part)
  }

  while (i < raw.length) {
    const afterComment = skipComment(raw, i)
    if (afterComment !== -1) {
      i = afterComment
      continue
    }
    const ch = raw[i]!
    if (QUOTES.has(ch)) {
      i = skipQuoted(raw, i)
      continue
    }
    if (ch === '(') parenDepth += 1
    else if (ch === ')') parenDepth -= 1
    else if (ch === '{') braceDepth += 1
    else if (ch === '}') braceDepth -= 1
    else if (ch === '[') bracketDepth += 1
    else if (ch === ']') bracketDepth -= 1
    else if (ch === ';' && parenDepth === 0 && braceDepth === 0 && bracketDepth === 0) {
      push(i)
      start = i + 1
    }
    i += 1
  }

  push(raw.length)
  return out
}
